import { SelectableBoxField } from '@flos/react-ui';
import {
    PetClaimsSelector as PetClaimsSelectorType,
    FormValues,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../../../state/appState';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { PetClaimsData } from '../../../types/services';
import { useEffect, useState } from 'react';
import { petClaimsMapping } from './mapping';
import { getAnimalType, AnimalType } from '../PetSelector/utils';

const NEW_CLAIM = 'new-claim';
const SUMMARY_LABEL_NEW_CLAIM = 'Dette er en ny skade';

const PetClaimSelector = ({ elementId, petTypeSelectorElementId }: PetClaimsSelectorType) => {
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);
    const petClaimsData = useAppState((state) => state.petClaimsData);

    const value = PetClaimSelector.getValueOrDefault(formValues, elementId);

    const selectedAnimalType: AnimalType = getAnimalType(formValues, petTypeSelectorElementId, undefined);

    const [petClaimsOptions, setPetClaimsOptions] = useState<
        { label: string; value: string; animalClaims: PetClaimsData[] }[]
    >([]);

    useEffect(() => {
        if (petClaimsData?.length) {
            const petClaims = petClaimsData
                .filter((petClaim) => petClaim.animalType === selectedAnimalType)
                .map((petClaim) => {
                    return {
                        label: getPetClaimLabel(petClaim),
                        value: petClaim.claimNumber,
                        animalClaims: petClaimsData
                    };
                });

            petClaims.push({
                label: 'Nej, det er en ny skade',
                value: NEW_CLAIM,
                animalClaims: []
            });
            setPetClaimsOptions(petClaims);
        }
    }, [formValues, petClaimsData, selectedAnimalType]);

    const getPetClaimLabel = (claim: PetClaimsData) => {
        const { animalType, animalName, causeOfLoss, dateOfLoss } = claim;

        // Format dateOfLoss to danish format YYYY-MM-DD > DD-MM-YYYY
        const dateOfLossFormatted = dateOfLoss.match(/\d{4}-\d{2}-\d{2}/)
            ? dateOfLoss.split('-').reverse().join('-')
            : dateOfLoss;
        return [petClaimsMapping[animalType], animalName, causeOfLoss, dateOfLossFormatted].join(', ');
    };

    const handleClaimAnimal = (value: string) => {
        if (value !== NEW_CLAIM) {
            const { dateOfLoss } = formValues;
            if (dateOfLoss) {
                setFormValue('dateOfLoss', undefined);
            }
        }
    };

    return (
        <>
            <SelectableBoxField
                options={petClaimsOptions}
                onChangeValue={(selectedValue: string) => {
                    const selectedOption = petClaimsOptions.find((option) => option.value === selectedValue);
                    setFormValue(elementId, selectedOption);
                    handleClaimAnimal(selectedValue);
                }}
                required
                value={value.value}
            />
        </>
    );
};

PetClaimSelector.isValid = (formValues: FormValues, elementId: string) => {
    const valueObject = PetClaimSelector.getValue(formValues, elementId);

    if (valueObject && valueObject.label !== '') {
        return true;
    }
    return false;
};

PetClaimSelector.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

PetClaimSelector.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = PetClaimSelector.getValue(formValues, elementId);

    if (value === undefined) {
        return { value: '', label: '', animalClaims: [] };
    }

    return value;
};

PetClaimSelector.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value = PetClaimSelector.getValue(formValues, elementId);

    if (value) {
        return value.label;
    }

    return '';
};

PetClaimSelector.getValueSubmit = (formValues: FormValues, elementId: string, caption: string) => {
    const valueObj = PetClaimSelector.getValueOrDefault(formValues, elementId);
    const valueStr = PetClaimSelector.getValueHuman(formValues, elementId);

    const { value: claimNumberExisting, animalClaims } = valueObj;
    const valueId = {
        animalClaims,
        claimNumberExisting
    };

    return getClaimFormSubmitValue('animalsicknessexistingclaimswidget', elementId, caption, valueStr, valueId);
};

PetClaimSelector.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as PetClaimsSelectorType;
    const { summaryCaption } = element;
    const value = PetClaimSelector.getValue(formValues, elementId);

    if (value === undefined) {
        return null;
    }

    const { label, value: selectedValue } = value;

    const summaryValueLabel = selectedValue === NEW_CLAIM ? SUMMARY_LABEL_NEW_CLAIM : label;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={summaryValueLabel} />;
};

export default PetClaimSelector;
